/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { colors, borderRadius } from '../../styles/theme';
import { FacebookIcon, XIcon, InstagramIcon, LinkedInIcon } from '../common';

const footerStyles = css`
  background: linear-gradient(135deg, ${colors.primary[800]} 0%, ${colors.primary[900]} 100%);
  color: white;
  padding: 4rem 0 2rem;

  @media (min-width: 768px) {
    padding: 5rem 0 2rem;
  }
`;

// Sayfa içerikleriyle aynı hizada olması için diğer container'larla aynı yatay boşluk
const containerStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const gridStyles = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1.6fr 1fr 1fr 1.2fr;
    gap: 3rem;
  }
`;

const brandSectionStyles = css`
  h3 {
    font-size: 1.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, white, ${colors.primary[200]});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    color: ${colors.primary[200]};
    font-size: 0.95rem;
    line-height: 1.7;
    max-width: 300px;
  }
`;

const socialLinksStyles = css`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;

  a {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: ${borderRadius.lg};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.2s ease;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      color: white;
      background: ${colors.primary[500]};
      transform: translateY(-2px);
    }
  }
`;

const sectionStyles = css`
  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    color: white;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  a {
    color: ${colors.primary[200]};
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;

    &:hover {
      color: white;
    }
  }
`;

const contactItemStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${colors.primary[200]};
  font-size: 0.9rem;
  margin-bottom: 0.75rem;

  svg {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    flex-shrink: 0;
    color: ${colors.primary[300]};
  }

  a {
    color: ${colors.primary[200]};
    text-decoration: none;

    &:hover {
      color: white;
    }
  }
`;

const dividerStyles = css`
  height: 1px;
  background: linear-gradient(90deg, transparent, ${colors.primary[600]}, transparent);
  margin: 3rem 0 2rem;
`;

const bottomStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const copyrightStyles = css`
  color: ${colors.primary[300]};
  font-size: 0.875rem;
  margin: 0;
`;

const paymentLogosStyles = css`
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0.8;

  img, svg {
    height: 24px;
    width: auto;
  }

  span {
    color: ${colors.primary[300]};
    font-size: 0.75rem;
  }
`;

// SVG İkonlar
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  </svg>
);

// Ödeme logoları
const VisaLogo = () => (
  <svg width="50" height="16" viewBox="0 0 750 471" fill="none">
    <path d="M278.198 334.228L311.058 138.766H364.578L331.718 334.228H278.198Z" fill="white" />
    <path d="M524.307 142.688C513.127 138.767 495.197 134.5 473.127 134.5C420.257 134.5 383.267 162.068 383.007 201.378C382.617 230.458 409.507 246.698 429.717 256.398C450.457 266.398 457.547 272.958 457.457 282.058C457.287 295.988 440.407 302.338 424.597 302.338C402.847 302.338 391.207 299.338 373.187 291.458L366.197 288.108L358.547 333.058C371.607 339.058 394.457 344.278 418.107 344.538C474.257 344.538 510.467 317.328 510.857 275.568C511.067 252.828 496.807 235.578 466.607 221.388C448.017 212.078 436.427 205.878 436.557 196.058C436.557 187.288 446.867 177.858 469.157 177.858C487.747 177.548 501.437 181.348 512.067 185.268L517.147 187.608L524.307 142.688Z" fill="white" />
    <path d="M661.615 138.766H620.465C607.505 138.766 597.835 142.546 592.075 155.866L512.365 334.228H568.365L579.965 302.858H648.245L655.005 334.228H704.135L661.615 138.766ZM596.085 260.828L618.355 203.198L628.935 201.458L642.045 260.828H596.085Z" fill="white" />
    <path d="M232.903 138.766L180.663 270.378L175.073 243.258C165.273 211.468 135.423 176.858 102.003 159.528L149.923 334.038H206.333L289.313 138.766H232.903Z" fill="white" />
  </svg>
);

const MasterCardLogo = () => (
  <svg width="40" height="24" viewBox="0 0 152 100" fill="none">
    <circle cx="50" cy="50" r="40" fill="#EB001B" />
    <circle cx="102" cy="50" r="40" fill="#F79E1B" />
    <path d="M76 22C66 30 59 42 59 55C59 68 66 80 76 88C86 80 93 68 93 55C93 42 86 30 76 22Z" fill="#FF5F00" />
  </svg>
);

export default function Footer() {
  return (
    <footer css={footerStyles}>
      <div css={containerStyles}>
        <div css={gridStyles}>
          {/* Marka Bölümü */}
          <div css={brandSectionStyles}>
            <h3>YolSepetiGO Blog</h3>
            <p>
              Yolda kaldığınızda yanınızda olan güvenilir çözüm ortağınız.
              Çekici, yol yardım, nakliye ve vinç hizmetleri hakkında en güncel
              bilgileri sizinle paylaşıyoruz.
            </p>
            <div css={socialLinksStyles}>
              <a href="https://facebook.com/yolsepetigo" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FacebookIcon /></a>
              <a href="https://twitter.com/yolsepetigo" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)"><XIcon /></a>
              <a href="https://instagram.com/yolsepetigo" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><InstagramIcon /></a>
              <a href="https://linkedin.com/company/yolsepetigo" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><LinkedInIcon /></a>
            </div>
          </div>

          {/* Hızlı Linkler */}
          <div css={sectionStyles}>
            <h4>Hızlı Linkler</h4>
            <ul>
              <li><Link to="/">Ana Sayfa</Link></li>
              <li><Link to="/blog">Tüm Yazılar</Link></li>
              <li><Link to="/kategoriler">Kategoriler</Link></li>
              <li><Link to="/hakkimizda">Hakkımızda</Link></li>
              <li><Link to="/sozlesmeler">Gizlilik Politikası & Sözleşmeler</Link></li>
            </ul>
          </div>

          {/* Hizmetler */}
          <div css={sectionStyles}>
            <h4>Hizmetler</h4>
            <ul>
              <li><Link to="/nasil-kullanilir/cekici">Çekici Hizmeti</Link></li>
              <li><Link to="/nasil-kullanilir/yol-yardim">Yol Yardım</Link></li>
              <li><Link to="/nasil-kullanilir/nakliye">Nakliye</Link></li>
              <li><Link to="/nasil-kullanilir/vinc">Vinç Hizmeti</Link></li>
              <li><Link to="/nasil-kullanilir/servis">Servis</Link></li>
              <li><Link to="/nasil-kullanilir/vip-tasima">VIP Taşıma</Link></li>
              <li><Link to="/nasil-kullanilir/surucu">Sürücü Olun</Link></li>
            </ul>
          </div>

          {/* İletişim */}
          <div css={sectionStyles}>
            <h4>İletişim</h4>
            <div css={contactItemStyles}>
              <PhoneIcon />
              <a href="tel:+905550103434">+90 555 010 34 34</a>
            </div>
            <div css={contactItemStyles}>
              <EmailIcon />
              <a href="mailto:info@yolsepetigo.com">info@yolsepetigo.com</a>
            </div>
            <div css={contactItemStyles}>
              <LocationIcon />
              <span>İstanbul, Türkiye</span>
            </div>
          </div>
        </div>

        <div css={dividerStyles} />

        <div css={bottomStyles}>
          <p css={copyrightStyles}>
            © {new Date().getFullYear()} YolSepetiGO. Tüm hakları saklıdır.
          </p>
          <div css={paymentLogosStyles}>
            <VisaLogo />
            <MasterCardLogo />
            <span>Güvenli Ödeme</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
